import toast, { type ToastOptions } from 'react-hot-toast';

const DEFAULT_OPTIONS: ToastOptions = {
  duration: 3800,
  className: 'ne-toast',
};

function gasSymbol(): string {
  const network = (import.meta.env.VITE_NETWORK || 'mordor').toLowerCase();
  return network === 'mainnet' ? 'ETC' : 'METC';
}

function networkLabel(): string {
  const network = (import.meta.env.VITE_NETWORK || 'mordor').toLowerCase();
  return network === 'mainnet' ? 'ETC Mainnet' : 'ETC Mordor';
}

function friendlyError(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!error) return fallback;

  const raw =
    typeof error === 'string'
      ? error
      : (error as { reason?: string; shortMessage?: string; message?: string })?.reason ||
        (error as { shortMessage?: string })?.shortMessage ||
        (error as { message?: string })?.message ||
        '';

  const message = String(raw || '').trim();
  if (!message) return fallback;

  const lower = message.toLowerCase();

  if (
    lower.includes('user rejected') ||
    lower.includes('user denied') ||
    lower.includes('rejected the request') ||
    lower.includes('action_rejected')
  ) {
    return 'Transaction cancelled';
  }

  if (lower.includes('insufficient funds') || lower.includes('insufficient balance')) {
    return `Not enough ${gasSymbol()} for gas`;
  }

  if (lower.includes('transfer amount exceeds balance') || lower.includes('exceeds balance')) {
    return 'Not enough ETCPOWv2 balance';
  }

  if (lower.includes('allowance') || lower.includes('insufficient allowance')) {
    return 'Token approval required — try again';
  }

  if (lower.includes('network') && (lower.includes('wrong') || lower.includes('unsupported'))) {
    return `Wrong network — switch to ${networkLabel()}`;
  }

  if (lower.includes('nonce') || lower.includes('replacement transaction')) {
    return 'Wallet busy — wait a moment and retry';
  }

  if (lower.includes('timeout') || lower.includes('timed out')) {
    return 'Network timeout — please retry';
  }

  if (lower.includes('pool') && (lower.includes('underfund') || lower.includes('insufficient'))) {
    return 'Rewards pool underfunded — try again later';
  }

  // Strip noisy ethers / RPC prefixes for anything else short enough
  const cleaned = message
    .replace(/^error:\s*/i, '')
    .replace(/^execution reverted:\s*/i, '')
    .replace(/^call exception.*?:\s*/i, '')
    .split('\n')[0]
    .trim();

  if (cleaned.length > 0 && cleaned.length <= 120 && !cleaned.includes('{')) {
    return cleaned;
  }

  return fallback;
}

export const notify = {
  success: (message: string, options?: ToastOptions) =>
    toast.success(message, {
      ...DEFAULT_OPTIONS,
      className: 'ne-toast ne-toast-success',
      ...options,
    }),

  error: (error: unknown, fallback?: string, options?: ToastOptions) =>
    toast.error(friendlyError(error, fallback), {
      ...DEFAULT_OPTIONS,
      duration: 4500,
      className: 'ne-toast ne-toast-error',
      ...options,
    }),

  loading: (message: string, options?: ToastOptions) =>
    toast.loading(message, {
      ...DEFAULT_OPTIONS,
      className: 'ne-toast ne-toast-loading',
      ...options,
    }),

  info: (message: string, options?: ToastOptions) =>
    toast(message, {
      ...DEFAULT_OPTIONS,
      className: 'ne-toast ne-toast-blank',
      ...options,
    }),

  dismiss: (id?: string) => toast.dismiss(id),
};

export { friendlyError };
